/**
 * NBER US recession bands as Plotly `shapes` entries.
 *
 * Feature code composes these into a chart's `layout.shapes` array.
 */
import type { Shape } from 'plotly.js';

type DateRange = readonly [string, string];

/** NBER US recession peak → trough dates. */
const RECESSIONS: readonly DateRange[] = [
  ['1957-08-01', '1958-04-30'],
  ['1960-04-01', '1961-02-28'],
  ['1969-12-01', '1970-11-30'],
  ['1973-11-01', '1975-03-31'],
  ['1980-01-01', '1980-07-31'],
  ['1981-07-01', '1982-11-30'],
  ['1990-07-01', '1991-03-31'],
  ['2001-03-01', '2001-11-30'],
  ['2007-12-01', '2009-06-30'],
  ['2020-02-01', '2020-04-30'],
];

export interface RecessionShapeOptions {
  /** Only include bands overlapping `[start, end]`. */
  plotXRange?: readonly [string | Date, string | Date];
  /** Override the fill color (default `#E8E8E8`). */
  fillcolor?: string;
  /** Override the opacity (default `0.5`). */
  opacity?: number;
}

function overlaps(range: DateRange, plot: readonly [Date, Date]): boolean {
  const [start, end] = range;
  return new Date(end) >= plot[0] && new Date(start) <= plot[1];
}

export function getRecessionShapes(options: RecessionShapeOptions = {}): Partial<Shape>[] {
  const { plotXRange, fillcolor = '#E8E8E8', opacity = 0.5 } = options;

  let filtered: readonly DateRange[] = RECESSIONS;
  if (plotXRange) {
    const bounds: [Date, Date] = [new Date(plotXRange[0]), new Date(plotXRange[1])];
    filtered = RECESSIONS.filter((range) => overlaps(range, bounds));
  }

  return filtered.map(([start, end]) => ({
    type: 'rect',
    x0: start,
    x1: end,
    y0: 0,
    y1: 1,
    yref: 'paper',
    fillcolor,
    opacity,
    layer: 'below',
    line: { width: 0 },
  }));
}
